/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2022
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import PropertySetter from './PropertySetter';
import TreatAs from './TreatAs';
import MoserObjectUtils from './MoserObjectUtils';
import EditModeType from './EditModeType';
import MoserObject from './MoserObject';
import EditModeUtils from './EditModeUtils';
import MoserCommonUtils from './MoserCommonUtils';
import QsClassifierType from './QsClassifierType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (static) // isShortcutOrAlias
//@import com/ibm/bi/platform/moser/common/generated/metadata/TreatAs (static) // TreatAs
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getItems
//@import com/ibm/bi/platform/moser/common/generated/metadata/EditModeType (static) // EditModeType
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (static) // new
//@import com/ibm/bi/platform/moser/common/utils/EditModeUtils (static) // setEditMode
//@import com/ibm/bi/platform/moser/common/utils/MoserCommonUtils (runtime) // getObject
//@import com/ibm/bi/platform/moser/common/generated/metadata/QsClassifierType (runtime) // QsClassifierType

/**
 * This class provides a set of static utility APIs for shortcuts and aliases. This class will be translated to JavaScript
 * @author  alan-tran
 */
export default class ShortcutUtils extends BaseMoserObject{

	/**
	 * Returns true if query subject is a shortcut or alias.
	 * @param {QuerySubject} qs              query subject
	 * @param {boolean} includeInvalid  include query subjects pointing to an invalid target
	 * @return {boolean}  t/f
	 */
	static isShortcutOrAlias(qs, includeInvalid) {
		return PropertySetter.isShortcutOrAlias(qs, includeInvalid);
	}

	/**
	 * Returns true if query subject is an alias
	 * @param {QuerySubject} qs              query subject
	 * @param {boolean} includeInvalid  include query subjects pointing to an invalid target
	 * @return {boolean}  t/f
	 */
	static isAlias(qs, includeInvalid) {
		if (!(ShortcutUtils.isShortcutOrAlias(qs, includeInvalid))) {
			return false;
		}
		return qs.getTreatAs() == TreatAs.ALIAS;
	}

	/**
	 * Returns true if query subject is a shortcut
	 * @param {QuerySubject} qs              query subject
	 * @param {boolean} includeInvalid  include query subjects pointing to an invalid target
	 * @return {boolean}  t/f
	 */
	static isShortcut(qs, includeInvalid) {
		if (!(ShortcutUtils.isShortcutOrAlias(qs, includeInvalid))) {
			return false;
		}
		return qs.getTreatAs() == TreatAs.REFERENCE;
	}

	/**
	 * Returns true if query subject is a well-defined shortcut or alias that has been resolved.
	 * @param {QuerySubject} qs
	 * @return {boolean}  t/f
	 */
	static isShortcutOrAliasValid(qs) {
		return ShortcutUtils.isShortcutOrAlias(qs, false);
	}

	/**
	 * Returns true if query subject is a well-defined shortcut that has been resolved.
	 * @param {QuerySubject} qs
	 * @return {boolean}  t/f
	 */
	static isShortcutValid(qs) {
		return ShortcutUtils.isShortcut(qs, false);
	}

	/**
	 * Returns true if query subject is a well-defined alias that has been resolved.
	 * @param {QuerySubject} qs
	 * @return {boolean}  t/f
	 */
	static isAliasValid(qs) {
		return ShortcutUtils.isAlias(qs, false);
	}

	/**
	 * Updates property override of each item in the shortcut and clears the property override.
	 * @param {QuerySubject} qs 	shortcut qs
	 */
	static cleanShortcutItemPropertyOverride(qs) {
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getItems(qs), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			if (obj.basicGetPropertyOverride() && !(obj.basicGetPropertyOverride().length==0)) {
				for (var i = obj.basicGetPropertyOverride().length - 1; i >= 0; --i) {
					obj.removePropertyOverrideAt(i);
				}
			}
		}
	}

	/**
	 * Updates parent in a list of ItemTypes to a shortcut
	 * @param {Array} items 	    list of ItemType
	 * @param {QuerySubject} shortcut 	shortcut qs
	 */
	static updateItemTypeParent(items, shortcut) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			item.setParent(shortcut);
		}
	}

	/**
	 * Copy essential properties from reference to shortcut.
	 * @param {QuerySubject} shortcut   obj to copy properties to.
	 * @param {QuerySubject} reference  qs shortcut is pointing to.
	 */
	static copyPropertiesFromReference(shortcut, reference, ignoreList) {
		shortcut.setEditMode(EditModeType.RO);
		try {
			var refClone = reference.clone();
			var scPropOverride = shortcut.basicGetPropertyOverride();
			var featureList = refClone.getFeatureList(null);
			for (var __i_enFor0 = 0, __exp_enFor0 = featureList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var feature = __exp_enFor0[__i_enFor0];
				if (ignoreList.indexOf(feature.getFeatureID())> -1 || scPropOverride && scPropOverride.indexOf(feature.getFeatureName())> -1 && !(ShortcutUtils.isShortcutQSPropRO(feature.getFeatureID()))) {
					continue;
				}
				if (!(feature.getIsList())) {
					var obj = refClone.basicGet(feature.getFeatureID());
					shortcut.set(feature.getFeatureID(), obj);
					if (obj instanceof MoserObject) {
						EditModeUtils.setEditMode(obj, EditModeType.RO);
						(obj).setParent(shortcut);
					}
				}
			}
		} catch (e) {
			throw e;
		}

		shortcut.setEditMode(EditModeType.RWPARTIAL);
	}

	/**
	 * Returns a combined ordered list of item types. Combines target items with extended items. Places extended items before, between, or after target or extended items. Uses the positionAfter property from extension item to place items.
	 * @param {Array} extensionItems 		extended items from a shortcut.
	 * @param {Array} targetItems 		items to be shared to the shortcut from target.
	 * @return {Array}  list of item types.
	 */
	static resolveExtensionPositions(extensionItems, targetItems) {
		var indexTop = 0;
		var orderedList = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = targetItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			orderedList.push(item);
		}
		var extendedList = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = extensionItems, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var item = __exp_enFor1[__i_enFor1];
			extendedList.push(item);
		}
		var idToObjectMap = {};
		for (var __i_enFor2 = 0, __exp_enFor2 = targetItems, __len_enFor2 = __exp_enFor2.length;
				__i_enFor2 < __len_enFor2; ++__i_enFor2) {
			var item = __exp_enFor2[__i_enFor2];
			var objectType = MoserCommonUtils.getObject(item);
			if (objectType && !(idToObjectMap.hasOwnProperty(objectType.getIdentifier()))) {
				idToObjectMap[objectType.getIdentifier()] = item;
			}
		}
		var changeFlag;
		do {
			changeFlag = false;
			var i = 0;
			while (i < extendedList.length) {
				var extension = extendedList[i];
				var objectTypeExt = MoserCommonUtils.getObject(extension);
				var positionAfter = extension.getPositionAfter();
				if (positionAfter != null) {
					var postionAfterObject = idToObjectMap[positionAfter];
					var index = orderedList.indexOf(postionAfterObject);
					if (index != -1) {
						orderedList.splice(index + 1, 0, extension);
						extendedList.splice(i, 1);
						i--;
						extension.setPositionAfter(null);
						if (!(idToObjectMap.hasOwnProperty(objectTypeExt.getIdentifier()))) {
							idToObjectMap[objectTypeExt.getIdentifier()] = extension;
						}
						changeFlag = true;
					}
				} else {
					orderedList.splice(indexTop++, 0, extension);
					extendedList.splice(i, 1);
					i--;
					if (!(idToObjectMap.hasOwnProperty(objectTypeExt.getIdentifier()))) {
						idToObjectMap[objectTypeExt.getIdentifier()] = extension;
					}
					changeFlag = true;
				}
				i++;
			}
			if (!changeFlag && !(extendedList.length==0)) {
				var extension = extendedList[0];
				extendedList.splice(0, 1);
				extension.setPositionAfter(null);
				var objectType = MoserCommonUtils.getObject(extension);
				orderedList.push(extension);
				if (!(idToObjectMap.hasOwnProperty(objectType.getIdentifier()))) {
					idToObjectMap[objectType.getIdentifier()] = extension;
				}
				changeFlag = true;
			}
		} while (changeFlag);
		return orderedList;
	}

	/**
	 * Returns true if the query subject should be inserted as a shortcut.
	 * @param  srcQs  source query subject to be inserted
	 */
	static insertAsShortcut(srcQS) {
		var containsLocalRef = false;
		var refs = srcQS.basicGetRef();
		if (refs && !(refs.length==0)) {
			containsLocalRef = true;
			for (var __i_enFor0 = 0, __exp_enFor0 = refs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var ref = __exp_enFor0[__i_enFor0];
				var parts = ref.split(new RegExp("\\."));
				if (parts.length != 1) {
					containsLocalRef = false;
					break;
				}
			}
		}
		var isValidShortcutOrAlias = ShortcutUtils.isShortcutOrAliasValid(srcQS);
		var isView = containsLocalRef && !isValidShortcutOrAlias;
		var isSetOperation = QsClassifierType.OPERATION_QUERY_SUBJECT == srcQS.getClassifier();
		var isJoinedView = QsClassifierType.JOIN_QUERY_SUBJECT == srcQS.getClassifier();
		return isValidShortcutOrAlias || isView || isSetOperation || isJoinedView;
	}

	/**
	 * Returns true if the property is not editable in a shortcut.
	 * @param {int} featureId   property feature id.
	 */
	static isShortcutQSPropRO(featureId) {
		switch (featureId) {
		case 181:
		case 48:
		case 180:
		case 182:
		case 17:
		case 16:
			return false;
		default:
			return true;
		}
	}

	/**
	 * Returns true if the property is not editable in an alias.
	 * @param {int} featureId   property feature id.
	 */
	static isAliasQSPropRO(featureId) {
		switch (featureId) {
		case 181:
		case 48:
		case 33:
		case 180:
		case 182:
		case 17:
		case 16:
			return false;
		default:
			return true;
		}
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ShortcutUtils.js